import { API } from '../config'
import {
    hasAnalyticsConsent,
    getClientId,
    getSessionId,
    refreshSession,
    ensureTrackingCookies,
    generateUuid,
} from '../helpers/trackingConsent'

const MAX_QUEUE = 20
const FLUSH_INTERVAL_MS = 10000
const MAX_RETRIES = 3

let queue = []
let lastSearchEventId = null
let intervalStarted = false

let detailStart = null
let detailActivityId = null
let detailSent = false

function nowIso() {
    return new Date().toISOString()
}

function enqueue(eventType, payload, immediate = false) {
    try {
        if (!hasAnalyticsConsent()) return null
        ensureTrackingCookies()
        refreshSession()

        const eventId = generateUuid()
        queue.push({
            event_id: eventId,
            event_type: eventType,
            timestamp: nowIso(),
            payload: payload || {},
        })

        startInterval()

        if (immediate || queue.length >= MAX_QUEUE) {
            flush()
        }

        return eventId
    } catch (e) {
        return null
    }
}

async function sendBatch(events, attempt = 0) {
    const clientId = getClientId()
    const sessionId = getSessionId()
    if (!clientId || !sessionId) return

    const body = JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        events,
    })

    try {
        const response = await fetch(API.TRACKING_EVENTS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
        })
        if (!response.ok) throw new Error('bad status')
    } catch (e) {
        if (attempt < MAX_RETRIES) {
            const delay = Math.pow(2, attempt + 1) * 1000
            setTimeout(() => sendBatch(events, attempt + 1), delay)
        }
    }
}

function flush(useBeacon = false) {
    try {
        if (!hasAnalyticsConsent() || queue.length === 0) return

        const clientId = getClientId()
        const sessionId = getSessionId()
        if (!clientId || !sessionId) {
            queue = []
            return
        }

        const events = queue.splice(0, queue.length)

        if (useBeacon && navigator?.sendBeacon) {
            const blob = new Blob(
                [JSON.stringify({ client_id: clientId, session_id: sessionId, events })],
                { type: 'application/json' }
            )
            navigator.sendBeacon(API.TRACKING_EVENTS, blob)
            return
        }

        sendBatch(events)
    } catch (e) {}
}

function startInterval() {
    if (intervalStarted) return
    intervalStarted = true
    setInterval(() => {
        if (queue.length > 0) flush()
    }, FLUSH_INTERVAL_MS)

    window.addEventListener('pagehide', () => flush(true))
    window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            stopDetailTimer()
            flush(true)
        }
    })
}

function track(eventType, payload) {
    return enqueue(eventType, payload)
}

function trackSearch(payload, hasResults) {
    const eventType = hasResults ? 'search' : 'search_no_results'
    const eventId = enqueue(eventType, payload, true)
    if (eventId) lastSearchEventId = eventId
    return eventId
}

function trackResultClick(activityId, positionInList) {
    return enqueue('result_click', {
        activity_id: activityId,
        position_in_list: positionInList,
        search_event_id: lastSearchEventId,
    }, true)
}

function trackMapPinClick(activityId) {
    return enqueue('map_pin_click', {
        activity_id: activityId,
        search_event_id: lastSearchEventId,
    }, true)
}

function trackDetailView(activityId, activityMode) {
    return enqueue('detail_view', {
        activity_id: activityId,
        activity_mode: activityMode,
        search_event_id: lastSearchEventId,
    }, true)
}

function trackCartAdd(activityId, offerEventId) {
    return enqueue('cart_add', {
        activity_id: activityId,
        offer_event_id: offerEventId,
        search_event_id: lastSearchEventId,
    }, true)
}

function trackCartRemove(activityId, offerEventId) {
    return enqueue('cart_remove', {
        activity_id: activityId,
        offer_event_id: offerEventId,
    }, true)
}

function startDetailTimer(activityId) {
    detailStart = Date.now()
    detailActivityId = activityId
    detailSent = false
}

function stopDetailTimer() {
    if (detailStart === null || detailActivityId === null || detailSent) return

    let duration = Math.round((Date.now() - detailStart) / 1000)
    if (duration < 1) {
        detailStart = null
        detailActivityId = null
        return
    }
    if (duration > 1800) duration = 1800

    detailSent = true
    enqueue('detail_time', {
        activity_id: detailActivityId,
        duration_seconds: duration,
    })

    detailStart = null
    detailActivityId = null
}

export const trackingService = {
    track,
    flush,
    trackSearch,
    trackResultClick,
    trackMapPinClick,
    trackDetailView,
    trackCartAdd,
    trackCartRemove,
    startDetailTimer,
    stopDetailTimer,
}
